<template>
  <div class="word-book-page">
    <div class="page-header">
      <h1 class="page-title">
        <i class="el-icon-collection"></i>
        我的单词
      </h1>
      <p class="page-subtitle">管理您的个人单词库，查看学习进度</p>
    </div>

    <div class="word-book-content">
      <!-- 排序和分页控制 -->
      <div class="control-section">
        <div class="sort-controls">
          <el-radio-group v-model="sortBy" @change="handleSortChange">
            <el-radio-button label="alphabetical">字母顺序</el-radio-button>
            <el-radio-button label="familiarity">熟悉程度</el-radio-button>
          </el-radio-group>
        </div>
        <div class="page-controls">
          <span>每页单词数:</span>
          <el-select v-model="pageSize" @change="handlePageSizeChange">
            <el-option label="3个" :value="3"></el-option>
            <el-option label="6个" :value="6"></el-option>
            <el-option label="9个" :value="9"></el-option>
            <el-option label="12个" :value="12"></el-option>
            <el-option label="20个" :value="20"></el-option>
          </el-select>
          <el-button @click="refreshData">重新获取</el-button>
        </div>
        <div class="status-filters">
          <el-button 
            :type="selectedStatus === 'new' ? 'danger' : ''" 
            size="small" 
            @click="filterByStatus('new')"
          >
            生词
          </el-button>
          <el-button 
            :type="selectedStatus === 'learning' ? 'warning' : ''" 
            size="small" 
            @click="filterByStatus('learning')"
          >
            一般
          </el-button>
          <el-button 
            :type="selectedStatus === 'mastered' ? 'primary' : ''" 
            size="small" 
            @click="filterByStatus('mastered')"
          >
            熟词
          </el-button>
        </div>
      </div>

      <!-- 单词统计 -->
      <div class="word-stats">
        <div class="stat-item">
          <div class="stat-icon total">
            <i class="el-icon-document"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ wordStats.total }}</div>
            <div class="stat-label">总单词数</div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon mastered">
            <i class="el-icon-check"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ wordStats.mastered }}</div>
            <div class="stat-label">已掌握</div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon learning">
            <i class="el-icon-loading"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ wordStats.learning }}</div>
            <div class="stat-label">学习中</div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon new">
            <i class="el-icon-plus"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ wordStats.new }}</div>
            <div class="stat-label">新单词</div>
          </div>
        </div>
      </div>

      <!-- 单词列表 -->
      <div class="word-list-section">
        <div class="list-header">
          <h3>单词列表</h3>
        </div>

        <!-- 单词卡片列表 -->
        <div class="word-cards-container">
          <div v-for="word in filteredWords" :key="word.id" class="word-card" :class="getCardClass(word.status)">
            <div class="word-content">
              <h3 class="word-text">{{ word.word || word.english_word }}</h3>
              <p class="phonetic-text">{{ word.phonetic || word.phonetic_en || word.phonetic_us }}</p>
              <p class="meaning-text">{{ word.meaning || word.chinese_meaning }}</p>
                <div class="word-tags">
                <el-tag size="mini" :type="getLevelType(word.level || word.difficulty_level)">
                  {{ getLevelText(word.level || word.difficulty_level) }}
                </el-tag>
                <el-tag size="mini" :type="getStatusType(word.status)">
                  {{ getStatusText(word.status) }}
                </el-tag>
                </div>
              </div>
              <div class="word-actions">
                <el-button size="mini" @click="playPronunciation(word)">
                  <i class="el-icon-microphone"></i>
                </el-button>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div class="pagination">
          <el-pagination
            @current-change="handlePageChange"
            :current-page="currentPage"
            :page-size="pageSize"
            :total="totalWords"
            layout="total, prev, pager, next"
          />
          <div class="jump-to-page">
            <span>跳至</span>
            <el-input
              v-model="jumpPage"
              type="number"
              size="small"
              placeholder="页码"
              @keyup.enter.native="handleJumpToPage"
              class="jump-input"
            />
            <span>页</span>
            <el-button
              type="primary"
              size="small"
              @click="handleJumpToPage"
            >
              确定
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WordBookAPI from "@/api/wordBook";

export default {
  name: "WordBook",
  data() {
    return {
      loading: false,
      selectedStatus: 'all',
      currentPage: 1,
      pageSize: 3, // 默认每页3个单词
      sortBy: 'familiarity', // 排序方式：alphabetical, familiarity
      jumpPage: '', // 跳转页码
      wordStats: {
        total: 0,
        mastered: 0,
        learning: 0,
        new: 0,
      },
      words: [],
      totalWords: 0
    };
  },
  computed: {
    filteredWords() {
      return this.words; // 后端已经处理了筛选，这里直接返回
    }
  },
  async mounted() {
    await this.loadWordData();
    await this.loadWordStats();
  },
  
  methods: {
    // 加载单词数据
    async loadWordData() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          status: this.selectedStatus !== 'all' ? this.selectedStatus : undefined,
          sortBy: this.sortBy
        };
        
        const response = await WordBookAPI.getWordList(params);
        if (response.success) {
          this.words = response.data.words || response.data || [];
          this.totalWords = response.data.total || 0;
        } else {
          this.$message.error(response.message);
        }
      } catch (error) {
        this.$message.error('加载单词数据失败');
        console.error('加载单词数据失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // 加载统计数据
    async loadWordStats() {
      try {
        const response = await WordBookAPI.getStatistics();
        if (response.success) {
          this.wordStats = response.data || this.wordStats;
        } else {
          console.warn('获取统计数据失败:', response.message);
        }
      } catch (error) {
        console.error('加载统计数据失败:', error);
      }
    },
    
    // 分页处理
    handlePageChange(page) {
      this.currentPage = page;
      this.loadWordData();
    },
    
    // 跳转到指定页
    handleJumpToPage() {
      const page = parseInt(this.jumpPage);
      const maxPage = Math.ceil(this.totalWords / this.pageSize);
      
      if (!page || page < 1) {
        this.$message.warning('请输入有效的页码');
        return;
      }
      
      if (page > maxPage) {
        this.$message.warning(`页码不能超过 ${maxPage}`);
        return;
      }
      
      this.currentPage = page;
      this.loadWordData();
      this.jumpPage = ''; // 清空输入框
    },
    
    // 播放发音
    playPronunciation(word) {
      if (word.pronunciation_url) {
        // 使用音频工具函数
        const { playAudio } = require('@/utils/audioUtils');
        playAudio(word.pronunciation_url).catch(() => {
          this.$message.warning('音频播放失败');
        });
      } else {
        this.$message.warning('暂无发音文件');
      }
    },
    
    // 排序方式改变
    handleSortChange() {
      this.currentPage = 1;
      this.loadWordData();
    },
    
    // 每页单词数改变
    handlePageSizeChange() {
      this.currentPage = 1;
      this.loadWordData();
    },
    
    // 刷新数据
    refreshData() {
      this.loadWordData();
    },
    
    // 按状态筛选
    filterByStatus(status) {
      this.selectedStatus = status;
      this.currentPage = 1;
      this.loadWordData();
    },
    
    // 获取卡片样式类
    getCardClass(status) {
      return {
        'card-new': status === 'new',
        'card-learning': status === 'learning', 
        'card-mastered': status === 'mastered'
      };
    },
    getLevelType(level) {
      const typeMap = {
        easy: 'success',
        medium: 'warning',
        hard: 'danger',
      };
      return typeMap[level] || 'info';
    },
    getLevelText(level) {
      const textMap = {
        easy: '初级',
        medium: '中级',
        hard: '高级',
      };
      return textMap[level] || '未知';
    },
    getStatusType(status) {
      const typeMap = {
        mastered: 'success',
        learning: 'warning',
        new: 'info',
      };
      return typeMap[status] || 'info';
    },
    getStatusText(status) {
      const textMap = {
        mastered: '已掌握',
        learning: '学习中',
        new: '新单词',
      };
      return textMap[status] || '未知';
    },
    formatDate(date) {
      return date.toLocaleDateString('zh-CN');
    },
  },
};
</script>

<style scoped>
.word-book-page {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.page-title i {
  color: #dc143c;
}

.page-subtitle {
  color: #7f8c8d;
  font-size: 16px;
}

.search-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  display: flex;
  gap: 16px;
  align-items: center;
}

.search-bar {
  flex: 1;
}

.filter-options {
  display: flex;
  gap: 12px;
}

.word-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.stat-item {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.stat-icon.total { background: #3498db; }
.stat-icon.mastered { background: #27ae60; }
.stat-icon.learning { background: #f39c12; }
.stat-icon.new { background: #e74c3c; }

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1;
}

.stat-label {
  font-size: 14px;
  color: #7f8c8d;
  margin-top: 4px;
}

/* 控制区域 */
.control-section {
  background: white;
  padding: 15px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.sort-controls {
  display: flex;
  align-items: center;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-filters {
  display: flex;
  gap: 8px;
}

.word-list-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.list-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.list-actions {
  display: flex;
  gap: 8px;
}

/* 单词卡片容器 */
.word-cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

/* 单词卡片 */
.word-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.word-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.15);
}

/* 卡片状态样式 */
.card-new {
  border-left: 4px solid #F56C6C;
}

.card-learning {
  border-left: 4px solid #E6A23C;
}

.card-mastered {
  border-left: 4px solid #67C23A;
}

.word-content {
  flex: 1;
}

.word-text {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
  text-align: center;
}

.phonetic-text {
  font-size: 16px;
  color: #666;
  text-align: center;
  margin-bottom: 10px;
  font-style: italic;
}

.meaning-text {
  font-size: 18px;
  color: #555;
  text-align: center;
  margin-bottom: 15px;
}

.word-tags {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 15px;
}

.word-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.word-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.word-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
}

.word-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.word-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.word-text {
  flex: 1;
}

.word-text h4 {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.word-text p {
  color: #7f8c8d;
  font-size: 14px;
  margin-bottom: 8px;
}

.word-tags {
  display: flex;
  gap: 8px;
}

.word-actions {
  display: flex;
  gap: 8px;
}

.word-details {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  gap: 8px;
}

.detail-item .label {
  font-weight: 500;
  color: #7f8c8d;
  min-width: 60px;
}

.detail-item .value {
  color: #2c3e50;
}

.word-toggle {
  text-align: center;
  margin-top: 12px;
  cursor: pointer;
  color: #7f8c8d;
  font-size: 14px;
}

.word-toggle:hover {
  color: #409eff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 24px;
}

.jump-to-page {
  display: flex;
  align-items: center;
  gap: 8px;
}

.jump-to-page span {
  font-size: 14px;
  color: #606266;
}

.jump-input {
  width: 80px;
}

.jump-input >>> .el-input__inner {
  text-align: center;
}

@media (max-width: 768px) {
  .word-book-page {
    padding: 16px;
  }

  .search-section {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-options {
    justify-content: center;
  }

  .word-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .list-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .list-actions {
    justify-content: center;
  }

  .word-main {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .word-actions {
    justify-content: center;
  }

  .word-details {
    grid-template-columns: 1fr;
  }
}
</style>



























